/** @jsxImportSource @emotion/react */
import { DragEndEvent } from '@dnd-kit/core';
import { css } from '@emotion/react';
import { KaiProWebReactContext } from '@kaiizen/kai-pro-api-react-state/web';
import { ThemeProvider } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router';
import { materialTheme } from '../lib';
import './App.css';
import './typography.css';

import {
  AdminScanPage,
  AdminScanPageNew,
  Auth,
  ContractorClaimPage,
  ContractorDashboardPage,
  ContractorLinkLandingPage,
  ContractorsPage,
  DashboardPage,
  FloorPlanEditorDemoPage,
  IssueVideoUploadPage,
  IssueVideoUploadsAdminPage,
  MarketsPage,
  MobileIssueVideoHomePage,
  NotFoundPage,
  OrgProductsPage,
  OrgPropertyPageNew,
  OrgSettingsPage,
  ProjectPage,
  PropertiesPage,
  PropertyPage,
  ScanTemplatePage,
  ScanTemplatesPage,
  UserAccountPage,
  UsersPage,
} from './Pages';

import {
  SCAN_FOCUS_PANEL_PAGE_ORDER,
  type ScanFocusPanelPage,
} from '@/Client/Partials/FocusPanel';
import { BASE_NAV_ITEMS, DEFAULT_SIDEBAR_CONFIG, Sidebar } from '@/Client/Partials/Sidebar';
import { MobileWebWarning } from '@/Client/Widgets/MobileWebWarning';
import {
  ScanEditingContextProvider,
  useCurrentOrg,
  useFeatureFlags,
  useManageScanPhotosSync,
  usePreventNavigationDuringSync,
} from '@kaiizen/kai-pro-api-react-state';
import { ReportType } from '@kaiizen/kai-reports';
import { createWebRealtimeClient } from '../realtime';
import {
  ANALYTICS_EVENTS,
  initAnalytics,
  setNewExperienceAnalyticsContext,
  setUserContext,
  trackError,
  trackEvent,
} from './analytics';
import { initDatadog, setDatadogContext, setDatadogUser } from './datadog';
import { ADMIN_LEFT_NAV_WIDTH } from './Layout';
import { getMobileIssueVideoLandingState, logError } from './Lib';
import { isOptedIntoNewExperience } from './Lib/newExperienceLib';
import {
  ConfirmationModalProvider,
  DndProvider,
  FeatureFlagsModalProvider,
  FullScreenScanPartNotesProvider,
  SuperAdminToolsModalProvider,
  SuppliersModalProvider,
  ToastProvider,
  useAuth,
  useWindowSize,
} from './State';
import { NavigationGuardProvider } from './State/NavigationGuardProvider';
import { GlobalColorVarsCss, TYPOGRAPHY2 } from './Styles';
import { pxToRem } from './Styles/pxToRem';

const PUSHER_KEY = (import.meta as any).env.VITE_PUSHER_KEY as string | undefined;
const PUSHER_CLUSTER = 'us2';

const getAnalyticsPageName = (pathname: string) => {
  if (pathname.startsWith('/scan/')) return 'scan';
  if (pathname.startsWith('/project/')) return 'project';
  if (pathname.startsWith('/property/') || pathname.startsWith('/properties')) return 'property';
  if (pathname.startsWith('/template')) return 'scan_template';
  if (pathname.startsWith('/org-settings')) return 'organization_settings';
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/account')) return 'account';
  if (pathname.startsWith('/mobile')) return 'mobile';
  return pathname === '/' ? 'home' : 'other';
};

/**
 * Holds the scan's data above every `/scan/:id` route, so moving between them — a focus
 * panel tab, Compare, the back button — reuses the scan already loaded instead of tearing
 * the provider down and fetching it again. Only a change of `:id` remounts it.
 *
 * It has to sit on a parent route for that to hold: React keeps a component mounted across
 * a navigation only while it stays the same type in the same place in the tree, and the
 * matched child route below here is free to differ.
 */
const ScanEditingLayout = () => {
  const { id } = useParams();

  return id
    ? (
      <ScanEditingContextProvider scanId={id} userHasAdminScanAccess={true}>
        <Outlet />
      </ScanEditingContextProvider>
    )
    : <Navigate to='/' replace />;
};

const ScanRoute = ({
  review,
  focusPanel,
  reportType,
  progress = false,
}: {
  review?: boolean;
  focusPanel?: ScanFocusPanelPage;
  reportType?: ReportType;
  progress?: boolean;
}) => {
  const { artifactScanId, groupId, contractorId, taskId } = useParams();
  // newScopeBuilderFlow replaces the main scan editor and the /closeout route only.
  // Legacy document routes (/scope, /change-order, /punchlist) keep ScopePrintPreview.
  // The flag only makes the new experience available; users see it after opting in
  // (NewExperienceBanner's "TRY IT NOW" or the Account page toggle sets the cookie).
  const { featureFlags } = useFeatureFlags();
  const useNewScopeBuilderFlow = isOptedIntoNewExperience(featureFlags);
  const ScanPage = useNewScopeBuilderFlow ? AdminScanPageNew : AdminScanPage;

  return (
    <ScanPage
      scanGroupId={groupId}
      scanContractorId={contractorId}
      scanTaskId={taskId}
      artifactScanId={artifactScanId}
      review={review}
      focusPanel={focusPanel}
      reportType={reportType}
      progress={progress}
    />
  );
};

/** The flagged Project Overview is a dedicated entry point into the scan workspace. */
const TurnBasedProjectOverviewRoute = () => <AdminScanPageNew focusPanel='overview' />;

const GalleryGroupRouteRedirect = () => {
  const { id, groupId, taskId } = useParams();
  const { search } = useLocation();
  const destination = id && groupId
    ? taskId
      ? `/scan/${id}/group/${groupId}/task/${taskId}/photos${search}`
      : `/scan/${id}/group/${groupId}/photos${search}`
    : undefined;
  return destination ? <Navigate to={destination} replace /> : <Navigate to='/' replace />;
};

/**
 * The five URL shapes a printable document takes, for one of the four report types. Paths are
 * relative to `/scan/:id` — see ScanEditingLayout, the route they all hang off.
 */
const scanReportRoutes = (segment: string, reportType: ReportType) => (
  <>
    <Route path={segment} element={<ScanRoute reportType={reportType} />} />
    <Route path={`${segment}/group/:groupId`} element={<ScanRoute reportType={reportType} />} />
    <Route
      path={`${segment}/group/:groupId/task/:taskId`}
      element={<ScanRoute reportType={reportType} />}
    />
    <Route
      path={`${segment}/group/:groupId/task/:taskId/option/:optionId`}
      element={<ScanRoute reportType={reportType} />}
    />
    <Route
      path={`${segment}/group/:groupId/task/:taskId/option/:optionId/lineitem/:lineItemId`}
      element={<ScanRoute reportType={reportType} />}
    />
  </>
);

/**
 * The scan routes both experiences serve, at the paths they have always had. Which page each
 * one draws is ScanRoute's business, and it reads the beta opt-in to decide.
 */
const sharedScanRoutes = () => (
  <>
    <Route index element={<ScanRoute />} />
    <Route path='group/:groupId' element={<ScanRoute />} />
    <Route path='group/:groupId/task/:taskId' element={<ScanRoute />} />
    {
      /* Contractor-bucketed selection — the Contractor tab's counterpart to /group.
        Focus-panel variants are registered in betaScanRoutes, alongside the group ones. */
    }
    <Route path='contractor/:contractorId' element={<ScanRoute />} />
    <Route path='contractor/:contractorId/task/:taskId' element={<ScanRoute />} />
    <Route path='review' element={<ScanRoute review />} />
    {
      /* The legacy progress page's group and task shapes, which both experiences have always
        served. Only its scope-level `/progress` differs between them — see legacyScanRoutes. */
    }
    <Route path='progress/group/:groupId' element={<ScanRoute progress />} />
    <Route path='progress/group/:groupId/task/:taskId' element={<ScanRoute progress />} />
    {scanReportRoutes('scope', 'fullScope')}
    {scanReportRoutes('change-order', 'changeOrder')}
    {scanReportRoutes('punchlist', 'punchlist')}
    {scanReportRoutes('closeout', 'closeout')}
  </>
);

/**
 * The focus-panel workspace: the beta's scan experience, and the only routes here a reader
 * opted out of the beta never sees. Each panel takes all three scope shapes — whole scope, a
 * group, a task — and `progress` among them is the redesign's Progress panel, which is why
 * the legacy progress page keeps its own routes in `legacyScanRoutes` rather than this list.
 */
const betaScanRoutes = () => (
  <>
    <Route
      path='artifact/:artifactScanId'
      element={<ScanRoute focusPanel='artifact' />}
    />
    {SCAN_FOCUS_PANEL_PAGE_ORDER.filter(focusPanelPage => focusPanelPage !== 'overview').flatMap(focusPanelPage => [
      <Route
        key={`scan-focus-${focusPanelPage}`}
        path={focusPanelPage}
        element={<ScanRoute focusPanel={focusPanelPage} />}
      />,
      <Route
        key={`scan-focus-group-${focusPanelPage}`}
        path={`group/:groupId/${focusPanelPage}`}
        element={<ScanRoute focusPanel={focusPanelPage} />}
      />,
      <Route
        key={`scan-focus-task-${focusPanelPage}`}
        path={`group/:groupId/task/:taskId/${focusPanelPage}`}
        element={<ScanRoute focusPanel={focusPanelPage} />}
      />,
      <Route
        key={`scan-focus-contractor-${focusPanelPage}`}
        path={`contractor/:contractorId/${focusPanelPage}`}
        element={<ScanRoute focusPanel={focusPanelPage} />}
      />,
      <Route
        key={`scan-focus-contractor-task-${focusPanelPage}`}
        path={`contractor/:contractorId/task/:taskId/${focusPanelPage}`}
        element={<ScanRoute focusPanel={focusPanelPage} />}
      />,
    ])}
    {
      /* Gallery used to be addressed with the scope ahead of the page; both spellings still
        arrive, so the older one is sent along to the shape every panel now takes. */
    }
    <Route path='photos/group/:groupId' element={<GalleryGroupRouteRedirect />} />
    <Route
      path='photos/group/:groupId/task/:taskId'
      element={<GalleryGroupRouteRedirect />}
    />
  </>
);

/**
 * Routes a reader opted out of the beta needs, that `betaScanRoutes` doesn't register the same way:
 * - `progress`: Progress as it was before the redesign, a page of its own rather than a focus
 *   panel. Opted in, that same URL is served by `betaScanRoutes` as the Progress panel.
 * - `photos`: the scan-wide photo gallery (`AllScanPhotosLegacy`). Opted in, the equivalent lives
 *   at the same path but is served as a focus panel by `betaScanRoutes` instead.
 */
const legacyScanRoutes = () => (
  <>
    <Route path='progress' element={<ScanRoute progress />} />
    <Route path='photos' element={<ScanRoute focusPanel='photos' />} />
  </>
);

const ScanTemplateRoute = ({ allTasks }: { allTasks?: boolean }) => {
  const { id, groupId } = useParams();
  return id
    ? (
      <ScanTemplatePage
        scanTemplateId={id}
        groupId={groupId}
        allTasks={allTasks}
      />
    )
    : <Navigate to='/' replace />;
};

const LegacyOrgSettingsRouteRedirect = () => {
  const { section } = useParams<{ section?: string }>();
  return section ? <Navigate to={`/org-settings/${section}`} replace /> : <Navigate to='/org-settings' replace />;
};

const MAIN_TYPOGRAPHY_STYLE = css`
  label: MAIN_TYPOGRAPHY_STYLE;
  ${TYPOGRAPHY2.xSmall}
  width: 100%;
  height: 100%;
  color: var(--text);
`;

const MAIN_LAYOUT_STYLE = css`
  label: MAIN_LAYOUT_STYLE;
  display: flex;
  flex-direction: row;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const CONTENT_SECTION_STYLE = css`
  label: CONTENT_SECTION_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: ${pxToRem(ADMIN_LEFT_NAV_WIDTH)};
  height: 100vh;
  overflow: hidden;
`;

const CONTENT_SECTION_STYLE_MOBILE = css`
  label: CONTENT_SECTION_STYLE_MOBILE;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100vh;
  overflow: hidden;
`;

const MOBILE_BREAKPOINT = 900;

const MainLayout = ({ isMobile }: { isMobile: boolean }) => {
  const { credentials, isSuperAdmin } = useAuth();
  const { currentOrg } = useCurrentOrg();
  const { featureFlags, featureFlagsReady } = useFeatureFlags();
  const location = useLocation();
  const canAccessOrgSettings = currentOrg?.role === 'admin' || isSuperAdmin === true;
  const isScanRoute = location.pathname.startsWith('/scan/');
  const useNewScopeBuilderFlow = isOptedIntoNewExperience(featureFlags);
  const sidebarLogoHeight = isScanRoute && (useNewScopeBuilderFlow || (featureFlags.turnBasedHandoff && location.pathname.endsWith('/overview')))
    ? 56
    : undefined;
  const mobileIssueVideoLandingState = getMobileIssueVideoLandingState({
    isMobile,
    isVideoUploadEnabled: !!featureFlags.mobileVideoIssueLanding,
  });
  const lastDesktopPathRef = useRef('/dashboard');

  // Resizing below the breakpoint funnels every route to `/mobile`, replacing history as it
  // goes; resizing back up must not lose the page the user was actually on. Remember the last
  // real desktop path (never `/mobile` itself) so the `/mobile` route below can return there
  // instead of always falling back to the dashboard default.
  if (!isMobile && location.pathname !== '/mobile') {
    lastDesktopPathRef.current = `${location.pathname}${location.search}`;
  }

  if (!credentials.token) {
    if (location.pathname !== '/') {
      return <Navigate to='/' replace />;
    }
    return <Auth />;
  }

  if (!featureFlagsReady) {
    return null;
  }

  if (mobileIssueVideoLandingState.showLandingPage) {
    return (
      <div css={MAIN_LAYOUT_STYLE}>
        <section css={CONTENT_SECTION_STYLE_MOBILE} style={{ marginLeft: 0 }}>
          <Routes>
            <Route path='/' element={<Navigate to='/mobile' replace />} />
            <Route
              path='/dashboard'
              element={<Navigate to='/mobile' replace />}
            />
            <Route
              path='/dashboard/market/:id'
              element={<Navigate to='/mobile' replace />}
            />
            <Route
              path='/mobile'
              element={
                <MobileIssueVideoHomePage
                  showVideoUpload={mobileIssueVideoLandingState.showVideoUpload}
                />
              }
            />
            <Route
              path='/video-upload'
              element={mobileIssueVideoLandingState.showVideoUpload ? <IssueVideoUploadPage mobileStandalone /> : <Navigate to='/mobile' replace />}
            />
            <Route path='*' element={<Navigate to='/mobile' replace />} />
          </Routes>
        </section>
      </div>
    );
  }

  return (
    <div css={MAIN_LAYOUT_STYLE}>
      <Sidebar
        config={canAccessOrgSettings ? DEFAULT_SIDEBAR_CONFIG : { navItems: BASE_NAV_ITEMS }}
        logoHeight={sidebarLogoHeight}
      />
      <section css={CONTENT_SECTION_STYLE}>
        <Routes>
          <Route path='/' element={<Navigate to='/dashboard' replace />} />
          <Route
            path='/mobile'
            element={<Navigate to={lastDesktopPathRef.current} replace />}
          />
          <Route
            path='/org-settings'
            element={<Navigate to='/org-settings/inputs' replace />}
          />
          <Route path='/org-settings/:section' element={<OrgSettingsPage />} />
          <Route
            path='/admin'
            element={<Navigate to='/org-settings' replace />}
          />
          <Route
            path='/admin/:section'
            element={<LegacyOrgSettingsRouteRedirect />}
          />
          <Route path='/properties' element={<PropertiesPage />} />
          <Route path='/properties/:id' element={<PropertiesPage />} />
          <Route path='/markets' element={<MarketsPage />} />
          <Route path='/dashboard' element={<DashboardPage />} />
          <Route path='/dashboard/market/:id' element={<DashboardPage />} />
          <Route path='/market/:id' element={<MarketsPage />} />
          <Route path='/users' element={<UsersPage />} />
          <Route path='/video-upload' element={<IssueVideoUploadPage />} />
          <Route
            path='/video-uploads'
            element={<IssueVideoUploadsAdminPage />}
          />
          <Route path='/user/:id' element={<UsersPage />} />
          <Route path='/contractors' element={<ContractorsPage />} />
          <Route path='/contractor/:id' element={<ContractorsPage />} />
          <Route
            path='/contractor-dashboard'
            element={<ContractorDashboardPage />}
          />
          <Route path='/products' element={<OrgProductsPage />} />
          <Route path='/product/:id' element={<OrgProductsPage />} />
          {
            /* Every scan route is a child of this one, so the scan loads once per :id and
              survives every move between them. See ScanEditingLayout. */
          }
          <Route path='/scan/:id' element={<ScanEditingLayout />}>
            {sharedScanRoutes()}
            {useNewScopeBuilderFlow ? betaScanRoutes() : legacyScanRoutes()}
            {useNewScopeBuilderFlow && featureFlags.turnBasedHandoff && <Route path='overview' element={<TurnBasedProjectOverviewRoute />} />}
          </Route>
          <Route path='/template/:id' element={<ScanTemplateRoute />} />
          <Route
            path='/template/:id/all-tasks'
            element={<ScanTemplateRoute allTasks />}
          />
          <Route
            path='/template/:id/group/:groupId'
            element={<ScanTemplateRoute />}
          />
          <Route path='/templates' element={<ScanTemplatesPage />} />
          <Route path='/templates/:id' element={<ScanTemplatesPage />} />

          <Route path='/account' element={<UserAccountPage />} />
          <Route path='/floor-plan-editor-demo/*' element={<FloorPlanEditorDemoPage />} />
          {/* The redesigned property page is part of the new experience (opt-in cookie plus newScopeBuilderFlow). */}
          <Route path='/property/:id' element={useNewScopeBuilderFlow ? <OrgPropertyPageNew /> : <PropertyPage />} />
          <Route path='/project/:projectId' element={<ProjectPage />} />
          <Route
            path='/project/:projectId/scan/:scanId'
            element={<ProjectPage />}
          />
          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </section>
    </div>
  );
};

export const App = () => {
  const { initializing, credentials, userId } = useAuth();
  const { width } = useWindowSize();
  const { currentOrg } = useCurrentOrg();
  const { featureFlags } = useFeatureFlags();
  const { manageScanPhotosSync } = useManageScanPhotosSync();
  usePreventNavigationDuringSync();
  const didInitAnalyticsRef = useRef(false);
  const didInitRef = useRef(false);
  const lastTrackedTokenRef = useRef<string | undefined>(undefined);
  const isUsingNewExperience = isOptedIntoNewExperience(featureFlags);

  useEffect(() => {
    if (didInitAnalyticsRef.current) return;
    initAnalytics();
    didInitAnalyticsRef.current = true;
  }, []);

  useEffect(() => {
    if (!userId) return;
    manageScanPhotosSync().catch(error => logError('App-init manageScanPhotosSync failed', error));
  }, [manageScanPhotosSync, userId]);

  useEffect(() => {
    if (!credentials.token) return;
    if (!didInitRef.current) {
      initDatadog();
      didInitRef.current = true;
    }
    setUserContext({
      userId: credentials.user?.id,
      orgId: credentials.orgId,
      role: (currentOrg as any)?.role,
      userCreatedAt: credentials.user?.createdAt,
    });
    setNewExperienceAnalyticsContext(isUsingNewExperience);
    setDatadogContext({
      userId: credentials.user?.id,
      orgId: credentials.orgId,
      role: (currentOrg as any)?.role,
    });
    if (credentials.user?.id) {
      setDatadogUser({
        id: credentials.user.id,
        name: credentials.user.name,
        email: credentials.user.email,
        orgId: credentials.orgId,
      });
    }
    if (lastTrackedTokenRef.current !== credentials.token) {
      trackEvent(ANALYTICS_EVENTS.userLoginSuccess, {
        orgId: credentials.orgId,
        userId: credentials.user?.id,
        role: (currentOrg as any)?.role,
      });
      lastTrackedTokenRef.current = credentials.token;
    }
  }, [
    credentials.token,
    credentials.orgId,
    credentials.user?.id,
    currentOrg,
    credentials.user?.createdAt,
    credentials.user?.email,
    credentials.user?.name,
    isUsingNewExperience,
  ]);
  const location = useLocation();

  useEffect(() => {
    if (!credentials.token) return;
    trackEvent(ANALYTICS_EVENTS.pageViewed, { page: getAnalyticsPageName(location.pathname) });
  }, [credentials.token, location.pathname]);

  useEffect(() => {
    if (!credentials.token) return;
    const reportWindowError = (event: ErrorEvent) => trackError('window.error', event.error);
    const reportUnhandledRejection = (event: PromiseRejectionEvent) => trackError('window.unhandled_rejection', event.reason);
    window.addEventListener('error', reportWindowError);
    window.addEventListener('unhandledrejection', reportUnhandledRejection);
    return () => {
      window.removeEventListener('error', reportWindowError);
      window.removeEventListener('unhandledrejection', reportUnhandledRejection);
    };
  }, [credentials.token]);

  if (initializing) {
    return null;
  }
  // Public share pages render regardless of auth.
  if (
    location.pathname === '/contractor-link'
    || location.pathname === '/contractor-claim'
  ) {
    return (
      <div css={MAIN_TYPOGRAPHY_STYLE}>
        <Routes>
          <Route
            path='/contractor-link'
            element={<ContractorLinkLandingPage />}
          />
          <Route path='/contractor-claim' element={<ContractorClaimPage />} />
        </Routes>
      </div>
    );
  }
  if (!credentials.token) {
    if (location.pathname !== '/') {
      return <Navigate to='/' replace />;
    }
    return <Auth />;
  }

  const isMobile = width < MOBILE_BREAKPOINT;

  return (
    <div css={MAIN_TYPOGRAPHY_STYLE}>
      <Routes>
        <Route path='/*' element={<MainLayout isMobile={isMobile} />} />
      </Routes>
      <MobileWebWarning />
    </div>
  );
};

export const startApp = () => {
  const container = document.getElementById('root');
  if (!container) throw new Error('Failed to find root element');

  // Prevent browser default of navigating to dropped files when not on a drop zone
  document.addEventListener('dragover', e => e.preventDefault());
  document.addEventListener('drop', e => e.preventDefault());

  const handleGlobalDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Handle photo to task drop
    if (activeData?.type === 'photo' && overData?.type === 'task') {
      // Reserved for future global photo-drop handling.
    }
  };

  const RootProviders = () => {
    const realtimeClient = useMemo(
      () =>
        PUSHER_KEY && PUSHER_CLUSTER
          ? createWebRealtimeClient(PUSHER_KEY, PUSHER_CLUSTER)
          : undefined,
      [],
    );

    return (
      <KaiProWebReactContext realtimeClient={realtimeClient}>
        <BrowserRouter>
          <ToastProvider>
            <ConfirmationModalProvider>
              <FeatureFlagsModalProvider>
                <SuperAdminToolsModalProvider>
                  <SuppliersModalProvider>
                    <FullScreenScanPartNotesProvider>
                      <NavigationGuardProvider>
                        <DndProvider onDragEnd={handleGlobalDragEnd}>
                          <App />
                        </DndProvider>
                      </NavigationGuardProvider>
                    </FullScreenScanPartNotesProvider>
                  </SuppliersModalProvider>
                </SuperAdminToolsModalProvider>
              </FeatureFlagsModalProvider>
            </ConfirmationModalProvider>
          </ToastProvider>
        </BrowserRouter>
      </KaiProWebReactContext>
    );
  };

  createRoot(container).render(
    <ThemeProvider theme={materialTheme}>
      <GlobalColorVarsCss />
      <React.StrictMode>
        <RootProviders />
      </React.StrictMode>
    </ThemeProvider>,
  );
};
